import {
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import ShipByDateCell from 'common/components/ShipByDateCell';
import lineItemStatus from 'common/constants/lineItemStatus';
import orderFillType from 'common/constants/orderFillType';
import { useTheme } from '@mui/material/styles';
import { find, isBoolean, map } from 'lodash';
import PropTypes from 'prop-types';
import BatchShippingIcon, { batchShippingTooltip } from 'common/components/BatchShippingIcon';
import BusinessAddressIndicator from 'common/components/BusinessAddressIndicator';
import { useDispatch, useSelector } from 'react-redux';
import perfectRxTheme from 'common/theme/perfectRxTheme';
import FacilityAvatar from 'common/modules/FacilityAvatar';
import { updateBatchShipmentFilter } from 'store/thunks/batchShipmentThunk';
import { useNavigate } from 'react-router-dom';

const headCells = [
  {
    id: 'itemName',
    label: 'Drug Name',
    align: 'center',
  },
  {
    id: 'isOtc',
    label: 'Type',
    align: 'center',
  },
  {
    id: 'rxNumber',
    label: 'Rx Number',
    align: 'center',
  },
  {
    id: 'px',
    label: 'Px',
    align: 'left',
  },
  {
    id: 'lineItemStatus',
    label: 'Item Status',
    align: 'center',
  },
  {
    id: 'fillType',
    label: 'Fill Type',
    align: 'center',
  },
  {
    id: 'form',
    label: 'Form',
    align: 'center',
  },
  {
    id: 'strength',
    label: 'Strength',
    align: 'center',
  },
  {
    id: 'quantity',
    label: 'Quantity',
    align: 'center',
  },
  {
    id: 'shipByDate',
    label: 'Ship By Date',
    align: 'center',
  },
  {
    id: 'shippingOption',
    label: 'Shipping Option',
    align: 'center',
  },
];

const OrderVerificationLineItemsRow = ({ lineItemRow, lineItems, fulfillingPharmacyId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentTenantId = useSelector(({ auth }) => auth?.currentTenantId);
  const facilities = useSelector(({ pharmacy }) => pharmacy[currentTenantId]);
  const fillingFacility = find(
    facilities,
    (facility) => facility?.fulfillingPharmacyId === fulfillingPharmacyId
  );

  const rxPrefix = fillingFacility?.config?.rxPrefix;
  const facilityName = fillingFacility?.name;
  const facilityState = fillingFacility?.state;

  const shippingOptions = useSelector(({ systemConfigs }) => systemConfigs.shippingOptions || []);
  const theme = useTheme();

  const navigateToBatchShipmentQueue = (orderId) => {
    dispatch(
      updateBatchShipmentFilter({
        filter: { orderId },
      })
    );
    navigate('/batchShipments');
  };

  return (
    <TableContainer sx={{ mb: 2, p: 2, ml: 25 }} key={`lineItemRow-${lineItemRow}`}>
      <Table size='small'>
        <TableHead>
          <TableRow hover>
            {headCells.map((headCell) => (
              <TableCell align={headCell.align} key={headCell.id} sx={{ fontWeight: '600' }}>
                {headCell.label}
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>

        <TableBody>
          {map(lineItems, (item) => (
            <TableRow key={item.lineItemId} hover>
              <TableCell align='center'>{item.itemName}</TableCell>
              <TableCell align='center'>
                {isBoolean(item.isOtc) && item.isOtc ? 'OTC' : 'RX'}
              </TableCell>
              <TableCell align='center'>{item.rxNumber}</TableCell>
              <TableCell align='center'>
                {rxPrefix ? (
                  <FacilityAvatar
                    rxPrefix={rxPrefix}
                    facilityName={facilityName}
                    facilityState={facilityState}
                  />
                ) : (
                  <Grid />
                )}
              </TableCell>
              <TableCell align='center'>{lineItemStatus[item.lineItemStatus]}</TableCell>
              <TableCell align='center'>
                <Chip
                  sx={{
                    background:
                      item.fillType === 'REFILL'
                        ? theme.palette.info.dark
                        : theme.palette.success.dark,
                    color: theme.palette.primary.contrastText,
                    borderRadius: '5px',
                    height: '25px',
                    width: '70px',
                  }}
                  label={
                    <Typography sx={{ fontSize: '1.1em' }}>
                      {orderFillType[item.fillType]}
                    </Typography>
                  }
                />
              </TableCell>
              <TableCell align='center'>{item.form}</TableCell>
              <TableCell align='center'>{item.strength}</TableCell>
              <TableCell align='center'>{item.quantity}</TableCell>
              <TableCell align='center'>
                <ShipByDateCell shipByDate={item.shipByDate} tags={item.tags} />
              </TableCell>
              <TableCell align='center'>
                {item.shippingOption
                  ? find(shippingOptions, (option) => option.optionCode === item.shippingOption)
                      ?.shippingOption || item.shippingOption
                  : 'NA'}
              </TableCell>
              <TableCell align='center'>
                <Tooltip
                  title={
                    item.inBatchShipmentQueue
                      ? batchShippingTooltip({
                          isBusinessAddress: item.isBusinessAddress,
                          isClickable: true,
                        })
                      : ''
                  }
                  arrow
                  placement='top'
                >
                  {item.inBatchShipmentQueue ? (
                    <BatchShippingIcon
                      onClick={() => navigateToBatchShipmentQueue(lineItemRow)}
                      color={perfectRxTheme.palette.warning.warningColour}
                      showBadge={item.isBusinessAddress}
                    />
                  ) : (
                    <Grid>{item.isBusinessAddress && <BusinessAddressIndicator />}</Grid>
                  )}
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

OrderVerificationLineItemsRow.propTypes = {
  lineItemRow: PropTypes.string.isRequired,
  fulfillingPharmacyId: PropTypes.string.isRequired,
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      lineItemId: PropTypes.string,
      itemName: PropTypes.string,
      rxNumber: PropTypes.string,
      strength: PropTypes.string,
      form: PropTypes.string,
      quantity: PropTypes.string,
      fillType: PropTypes.string,
      needsByDate: PropTypes.string,
    })
  ).isRequired,
};

export default OrderVerificationLineItemsRow;
